import * as React from 'react';
import Grid from "@mui/material/Grid";
import {useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import useRootHook from "../../hooks/useRoot";
import helpers from "../../helpers/common";
import paymethods from "../../models/paymethods";
import paysystems from "../../models/paysystems";
import PayMethodForm from "./PayMethodForm";
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';
import {Alert, Button, Stack} from "@mui/material";

export default function PayMethodAdd(props) {
    const {setShowLoader} = useRootHook()
    const navigate = useNavigate()
    const [paySystemsData, setPaySystemsData] = useState([])
    const [mapper, setMapper] = useState({
        keyToFieldMapper: {},
        fieldToKeyMapper: {}
    })
    const [errors, setErrors] = useState({});
    const [fields, setFields] = useState({enabled: true, available: true, blocked: false})
    const [loadError, setLoadError] = useState(null)

    const loadPaySystems = () => {
        setLoadError(null)
        setShowLoader(true)
        paysystems.getData()
            .then((data) => {
                setShowLoader(false)
                if (!Array.isArray(data) || data.length === 0) {
                    setPaySystemsData([])
                    setLoadError('No payment systems returned from API (/iapi/paysystems).')
                    return
                }
                setPaySystemsData(data)
                setFields((prev) => ({...prev, paysystem_id: data[0].id}))
            })
            .catch((err) => {
                setShowLoader(false)
                setLoadError(err?.message || 'Failed to load payment systems (/iapi/paysystems).')
            })
    }

    useEffect(() => {
        loadPaySystems()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!paySystemsData || paySystemsData.length === 0) return
        setMapperByRecordID(paySystemsData[0].id)
    }, [paySystemsData])

    const setMapperByRecordID = (id) => {
        const record = paySystemsData.find(system => system.id === id)
        if (!record || !record.paymethod_field_mapper) return
        let recordMapper = {...record.paymethod_field_mapper}
        const systemKey = String(record.key || '').toLowerCase()

        // Backward-compatible mapper defaults for old paysystem records.
        if (systemKey.includes('avrepay')) {
            recordMapper = {
                ...recordMapper,
                onsiteS2S: recordMapper.onsiteS2S || 'var8',
                testMode: recordMapper.testMode || 'var9',
                s2sToken: recordMapper.s2sToken || 'var10',
                skipCapture: recordMapper.skipCapture || 'var11',
            }
        }
        if (systemKey.includes('affor')) {
            recordMapper = {
                ...recordMapper,
                onsiteS2S: recordMapper.onsiteS2S || 'var10',
            }
        }
        setMapper({keyToFieldMapper: recordMapper, fieldToKeyMapper: helpers.invertObject(recordMapper)})
    }

    const handleChange = (ev) => {
        const [, value, name] = helpers.getFormEventData(ev);

        if (name === 'paysystem_id') setMapperByRecordID(parseInt(value))

        setErrors({...errors, [name]: undefined});

        setFields((prevState) => {
            let newFields = {...prevState};
            newFields[name] = value;

            return newFields
        })
    }

    const handleBlur = (ev) => {
        const [, , name] = helpers.getFormEventData(ev)
        const newErrors = {...errors}
        const newFields = {...fields}

        typeof paymethods.fieldValidator[name] === 'function'
        && paymethods.fieldValidator[name](newFields, newErrors)
        setErrors(newErrors)
    }

    const handleSubmit = (ev) => {
        ev.preventDefault()
        const validationErrors = paymethods.methodValidator.add(fields)
        setErrors(validationErrors)

        if (Object.keys(validationErrors).length !== 0) return

        setShowLoader(true)
        paymethods.add(fields)
            .then(() => {
                setShowLoader(false)
                navigate('/paymethods')
            }).catch(() => setShowLoader(false))
    }

    return <React.Fragment>
        <Grid size={12}>
            <PageHeader title="Add payment method" breadcrumbs={[['Dashboard', '/'], ['Methods', '/paymethods'], ['Add', '/paymethods/add']]} />
        </Grid>
        <Grid size={12}>
            <SectionCard sx={{ display: 'flex', flexDirection: 'column' }}>
                {loadError ? (
                    <Stack spacing={2}>
                        <Alert severity="error">{loadError}</Alert>
                        <Button variant="outlined" onClick={loadPaySystems}>
                            Retry
                        </Button>
                    </Stack>
                ) : paySystemsData.length === 0 ? (
                    <Alert severity="info">Loading payment systems…</Alert>
                ) : (
                    <PayMethodForm
                        paySystemsData={paySystemsData}
                        mapper={mapper}
                        fields={fields}
                        errors={errors}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        handleSubmit={handleSubmit}
                    />
                )}
            </SectionCard>
        </Grid>
    </React.Fragment>
}